import React, { useState } from "react";
import { FileJson } from "lucide-react";
import BlockCard from "./BlockCard";
import AddContentMenu from "./AddContentMenu";
import ImportContentDialog from "./ImportContentDialog";

const ImportButton = ({ onClick, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    title="Paste a content JSON payload"
    className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-border bg-surface text-[12.5px] font-semibold text-text_secondary shadow-sm ${className}`}
  >
    <FileJson size={14} />
    Import JSON
  </button>
);

const ContentBuilder = ({
  blocks,
  selectedBlockId,
  onSelectBlock,
  onAddBlock,
  onImportBlocks,
  onDuplicateBlock,
  onDeleteBlock,
  onMoveBlockUp,
  onMoveBlockDown,
  onReorderBlock,
}) => {
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const handleDragStart = (id) => setDraggedId(id);

  const handleDragOver = (id) => {
    if (id !== draggedId) setDragOverId(id);
  };

  const handleDrop = (targetId) => {
    if (draggedId && draggedId !== targetId) {
      onReorderBlock(draggedId, targetId);
    }
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const importDialog = (
    <ImportContentDialog
      isOpen={importOpen}
      onClose={() => setImportOpen(false)}
      onImport={onImportBlocks}
      existingCount={blocks.length}
    />
  );

  if (blocks.length === 0) {
    return (
      <div className="max-w-3xl mx-auto py-10">
        <AddContentMenu variant="full" onAdd={(type) => onAddBlock(type, 0)} onImport={() => setImportOpen(true)} />
        {importDialog}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between gap-3 pb-3 mb-1 border-b border-border">
        <p className="text-[12px] text-text_muted">
          {blocks.length} block{blocks.length > 1 ? "s" : ""}
        </p>
        <ImportButton onClick={() => setImportOpen(true)} />
      </div>

      <div className="space-y-1">
        <AddContentMenu onAdd={(type) => onAddBlock(type, 0)} />
        {blocks.map((block, index) => (
          <React.Fragment key={block.id}>
            <BlockCard
              block={block}
              index={index}
              total={blocks.length}
              isSelected={block.id === selectedBlockId}
              isDragging={block.id === draggedId}
              isDragOver={block.id === dragOverId}
              onSelect={onSelectBlock}
              onDuplicate={onDuplicateBlock}
              onDelete={onDeleteBlock}
              onMoveUp={onMoveBlockUp}
              onMoveDown={onMoveBlockDown}
              onDragStart={handleDragStart}
              onDragOverBlock={handleDragOver}
              onDrop={handleDrop}
              onDragEnd={handleDragEnd}
            />
            <AddContentMenu onAdd={(type) => onAddBlock(type, index + 1)} />
          </React.Fragment>
        ))}
      </div>

      {importDialog}
    </div>
  );
};

export default ContentBuilder;
